import { Component, OnInit, Input } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AlertTranslateService } from "src/app/services/alert.service";
import { EventService } from "src/app/services/event.service";
import { DatePipe } from "@angular/common";

@Component({
  selector: "app-event-date-edit",
  templateUrl: "./event-date-edit.component.html",
  styleUrls: ["./event-date-edit.component.scss"],
  providers: [DatePipe],
})
export class EventDateEditComponent implements OnInit {
  editEventDateForm: FormGroup;
  submitted: boolean = false;
  @Input() event: any;
  eventDaysCount: number = 0;
  minDate: string;
  currentStartDate: string;
  currentFinalDate: string;
  previewStartDate: string;
  previewFinalDate: string;
  previewEventDays: number[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertTranslateService,
    private _event: EventService,
    public activeModal: NgbActiveModal,
    private datePipe: DatePipe
  ) {}

  ngOnInit() {
    this.eventDaysCount = this.event && this.event.eventDays
      ? this.event.eventDays.length
      : 1;
    this.minDate = this.formatDateForInput(new Date().getTime());
    this.currentStartDate = this.formatDisplayDate(this.event.startDate);
    this.currentFinalDate = this.formatDisplayDate(this.event.finalDate);

    this.editEventDateForm = this.formBuilder.group({
      startDate: [
        this.getDefaultStartDate(),
        [Validators.required, this.todayOrFutureValidator.bind(this)],
      ],
    });

    this.updatePreview();
    this.editEventDateForm.get("startDate").valueChanges.subscribe(() => {
      this.updatePreview();
    });
  }

  private formatDateForInput(timestamp: number): string {
    const date = new Date(timestamp);
    return this.datePipe.transform(date, "yyyy-MM-dd");
  }

  private formatDisplayDate(timestamp: number): string {
    return this.datePipe.transform(new Date(timestamp), "dd/MM/yyyy");
  }

  private getDefaultStartDate(): string {
    const eventStart = this.formatDateForInput(this.event.startDate);
    return eventStart >= this.minDate ? eventStart : this.minDate;
  }

  private todayOrFutureValidator(control) {
    if (!control.value || !this.minDate) return null;
    return control.value >= this.minDate ? null : { pastDate: true };
  }

  private getStartOfDay(dateValue: string): number {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
  }

  private getEndOfDay(timestamp: number): number {
    const date = new Date(timestamp);
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    ).getTime();
  }

  private buildEventDays(startDate: number): number[] {
    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < this.eventDaysCount; i++) {
      days.push(currentDate.getTime());
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }

  updatePreview() {
    const startDateValue = this.editEventDateForm.get("startDate").value;

    if (!startDateValue) {
      this.previewStartDate = null;
      this.previewFinalDate = null;
      this.previewEventDays = [];
      return;
    }

    const startDate = this.getStartOfDay(startDateValue);
    this.previewEventDays = this.buildEventDays(startDate);
    this.previewStartDate = this.formatDisplayDate(startDate);
    this.previewFinalDate = this.formatDisplayDate(
      this.previewEventDays[this.previewEventDays.length - 1]
    );
  }

  async onSubmit() {
    this.submitted = true;
    if (!this.editEventDateForm.valid) return;

    try {
      const eventDates = this.previewEventDays;
      const startDate = eventDates[0];
      const finalDate = this.getEndOfDay(eventDates[eventDates.length - 1]);

      await this._event.updateEventDates(this.event._id, startDate, finalDate, eventDates);

      this.alertService.success("EVENT-DATES-UPDATED");
      this.submitted = false;
      this.activeModal.close("updated");
    } catch (ex) {
      this.alertService.danger("ERROR-UPDATE-EVENT-DATES");
      this.submitted = false;
    }
  }

  get f() {
    return this.editEventDateForm.controls;
  }
}
